'use client';

/**
 * 系统角色列表页
 * 
 * @页面路径 /organization/roles/system-roles
 * @权限要求 role:read:*
 * @文档参考 docs/modules/organization/05-ui-interaction-spec.md
 * 
 * 核心功能：
 * - 角色列表展示（名称、代码、描述、成员数）
 * - 搜索功能（角色名称/代码）
 * - 角色CRUD操作（创建、编辑、删除）
 * - 跳转到角色详情页（查看权限、成员）
 * - 系统角色标记（不可删除）
 */

import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { useTranslation } from '@/hooks/useTranslation';
import { PageState } from '@/components/common/feedback/PageState';
import { Button } from '@/components/ui/button';
import { 
  Plus, 
  Search, 
  Users, 
  Shield,
  Lock
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from '@/lib/toast';
import { getRoles, deleteRole, type Role } from '@/services/api/organization';
import { ApiClientError } from '@/lib/api-client';

export default function SystemRolesPage() {
  const router = useRouter();
  const { t } = useTranslation();
  
  const [roles, setRoles] = useState<Role[]>([]);
  const [filteredRoles, setFilteredRoles] = useState<Role[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState<Role | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Load roles list
  useEffect(() => {
    loadRoles();
  }, []);

  // Search filter
  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredRoles(roles);
    } else {
      const query = searchQuery.toLowerCase();
      setFilteredRoles(
        roles.filter(
          (role) =>
            role.name.toLowerCase().includes(query) ||
            role.code.toLowerCase().includes(query) ||
            role.description?.toLowerCase().includes(query)
        )
      );
    }
  }, [searchQuery, roles]);

  const loadRoles = async () => {
    try {
      setLoading(true);
      const data = await getRoles();
      setRoles(data);
      setFilteredRoles(data);
    } catch (error) {
      console.error('Failed to load roles:', error);
      if (error instanceof ApiClientError) {
        toast.error(`${t.organization.roles.loadFailed}: ${error.message}`);
      } else {
        toast.error(t.organization.roles.loadFailed);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRole = () => {
    router.push('/organization/roles/system-roles/new');
  };

  const handleEditRole = (roleId: string) => {
    router.push(`/organization/roles/system-roles/${roleId}`);
  };

  const handleViewRole = (roleId: string) => {
    router.push(`/organization/roles/system-roles/${roleId}`);
  };

  const handleDeleteClick = (role: Role) => {
    setRoleToDelete(role);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!roleToDelete) return;

    try {
      setDeleting(true);
      await deleteRole(roleToDelete.id);
      toast.success(`${t.organization.roles.deleteSuccess}: "${roleToDelete.name}"`);
      setDeleteDialogOpen(false);
      setRoleToDelete(null);
      loadRoles();
    } catch (error) {
      console.error('Failed to delete role:', error);
      if (error instanceof ApiClientError) {
        toast.error(`${t.common.deleteFailed}: ${error.message}`);
      } else {
        toast.error(t.organization.roles.deleteFailed);
      }
    } finally {
      setDeleting(false);
    }
  };

  const hasSearch = searchQuery.trim().length > 0;
  const emptyTitle = hasSearch ? t.organization.roles.noMatchingRoles : t.organization.roles.noRoles;
  const emptyDescription = hasSearch ? t.organization.roles.tryOtherKeywords : t.organization.roles.noRolesHint;

  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: '#f7f8fa' }}>
      {/* Title bar with toolbar - Feishu style */}
      <div className="bg-white border-b" style={{ borderColor: '#e5e6eb' }}>
        <div className="px-6 h-12">
          <div className="flex items-center justify-between h-full">
            <h1 className="text-lg font-semibold" style={{ color: '#1f2329' }} data-testid="page-title">{t.organization.roles.title}</h1>
            <div className="flex items-center gap-3">
              {/* Search Input */}
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t.organization.roles.searchPlaceholder}
                  className="px-3 py-1.5 pl-9 rounded-lg text-sm outline-none transition-all"
                  style={{ 
                    width: '320px',
                    backgroundColor: '#f7f8fa',
                    color: '#1f2329',
                    border: 'none'
                  }}
                  onFocus={(e) => {
                    e.target.style.backgroundColor = '#eff0f2';
                    e.target.style.boxShadow = '0 0 0 2px rgba(51, 112, 255, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.backgroundColor = '#f7f8fa';
                    e.target.style.boxShadow = 'none';
                  }}
                  data-testid="role-search-input"
                />
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2" style={{ color: '#8f959e' }} />
              </div>
            </div>

            <div className="flex items-center gap-2">
              {/* Create Role Button */}
              <button
                onClick={handleCreateRole}
                className="px-4 py-1.5 rounded-lg text-sm font-medium text-white flex items-center transition-all shadow-sm"
                style={{ 
                  backgroundColor: '#3370ff',
                  boxShadow: '0 1px 2px rgba(51, 112, 255, 0.2)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#5087ff';
                  e.currentTarget.style.boxShadow = '0 2px 4px rgba(51, 112, 255, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#3370ff';
                  e.currentTarget.style.boxShadow = '0 1px 2px rgba(51, 112, 255, 0.2)';
                }}
                data-action="create-role"
              >
                <Plus className="w-4 h-4 mr-1" />
                {t.organization.roles.createRole}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content area */}
      <div className="flex-1 overflow-auto">
        <div className="px-6 pt-4 pb-6">
          {/* Roles List */}
          <div className="bg-white rounded-lg" style={{ border: '1px solid #e5e6eb' }} data-testid="role-table">
            <div className="overflow-x-auto">
              {loading ? (
              <div className="p-12 text-center" data-testid="page-loading">
                <div className="animate-spin rounded-full h-12 w-12 mx-auto" style={{ borderTop: '2px solid #3370ff', borderRight: '2px solid transparent' }}></div>
                <p className="mt-4" style={{ color: '#646a73' }}>{t.common.loading}</p>
              </div>
            ) : filteredRoles.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12" data-testid={searchQuery ? "empty-search" : "empty-state"}>
                <PageState
                  variant="empty"
                  title={emptyTitle}
                  description={emptyDescription}
                  actionLabel={hasSearch ? undefined : t.organization.roles.createFirstRole}
                  onAction={hasSearch ? undefined : handleCreateRole}
                  layout="center"
                  className="border-0 bg-transparent"
                />
              </div>
            ) : (
              <table className="min-w-full">
                <thead style={{ backgroundColor: '#f7f8fa', borderBottom: '1px solid #e5e6eb' }}>
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium" style={{ color: '#646a73' }}>
                      {t.organization.roleName}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium" style={{ color: '#646a73' }}>
                      {t.organization.roleCode}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium" style={{ color: '#646a73' }}>
                      {t.organization.description}
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium" style={{ color: '#646a73' }}>
                      {t.organization.roles.memberCount}
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium" style={{ color: '#646a73' }}>
                      {t.organization.roles.permissionCount}
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium" style={{ color: '#646a73' }}>
                      {t.organization.roles.type}
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium" style={{ color: '#646a73' }}>
                      {t.common.actions}
                    </th>
                  </tr>
                </thead>
                  <tbody style={{ backgroundColor: '#fff' }}>
                  {filteredRoles.map((role, index) => (
                    <tr 
                      key={role.id} 
                      className="transition-colors cursor-pointer"
                      style={{ borderBottom: index < filteredRoles.length - 1 ? '1px solid #f2f3f5' : 'none' }}
                      onClick={() => handleViewRole(role.id)}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#f7f8fa';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#fff';
                      }}
                      data-testid="role-row"
                    >
                      <td className="px-6 py-4" data-column="name">
                        <div className="flex items-center">
                          <div 
                            className="w-8 h-8 rounded flex items-center justify-center mr-3"
                            style={{ backgroundColor: '#e1eaff' }}
                          >
                            <Shield className="w-4 h-4" style={{ color: '#3370ff' }} />
                          </div>
                          <span className="text-sm font-medium" style={{ color: '#1f2329' }}>{role.name}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4" data-column="code">
                        <code className="text-xs px-2 py-1 rounded" style={{ backgroundColor: '#f2f3f5', color: '#646a73' }}>
                          {role.code}
                        </code>
                      </td>
                      <td className="px-6 py-4 text-sm max-w-xs truncate" style={{ color: '#646a73' }} data-column="description">
                        {role.description || <span style={{ color: '#c9cdd4' }}>-</span>}
                      </td>
                      <td className="px-6 py-4 text-center" data-column="userCount">
                        <div className="flex items-center justify-center">
                          <Users className="w-4 h-4 mr-1" style={{ color: '#8f959e' }} />
                          <span className="text-sm" style={{ color: '#1f2329' }}>{role.userCount || 0}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-center text-sm" style={{ color: '#1f2329' }} data-column="permissionCount">
                        {role.permissionCount || 0}
                      </td>
                      <td className="px-6 py-4 text-center" data-column="type">
                        {role.isBuiltIn ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium" style={{ backgroundColor: '#f2f3f5', color: '#646a73' }} data-testid="type-builtin">
                            <Lock className="w-3 h-3" />
                            {t.organization.roles.builtIn}
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium" style={{ backgroundColor: '#e1eaff', color: '#3370ff' }} data-testid="type-custom">
                            {t.organization.roles.custom}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-center gap-3">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewRole(role.id);
                            }}
                            className="text-sm font-medium transition-colors"
                            style={{ color: '#3370ff' }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.color = '#5087ff';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.color = '#3370ff';
                            }}
                            data-action="view"
                          >
                            {t.common.view}
                          </button>
                          {!role.isBuiltIn && (
                            <>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditRole(role.id);
                                }}
                                className="text-sm font-medium transition-colors"
                                style={{ color: '#3370ff' }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.color = '#5087ff';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.color = '#3370ff';
                                }}
                                data-action="edit-permissions"
                              >
                                {t.common.edit}
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteClick(role);
                                }}
                                className="text-sm font-medium transition-colors"
                                style={{ color: '#f54a45' }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.color = '#ff7875';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.color = '#f54a45';
                                }}
                                data-action="delete"
                              >
                                {t.common.delete}
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  </tbody>
              </table>
            )}
            </div>
          </div>

          {/* Statistics */}
          {!loading && filteredRoles.length > 0 && (
            <div className="mt-4 text-sm" style={{ color: '#8f959e' }}>
              {t.organization.roles.totalRoles.replace('{count}', filteredRoles.length.toString())}
              {searchQuery && ` (${t.organization.roles.filteredFrom.replace('{count}', roles.length.toString())})`}
            </div>
          )}

          {/* System Notes */}
          {!loading && (
            <div className="mt-6">
              <div className="bg-white rounded-lg p-5" style={{ border: '1px solid #e5e6eb' }}>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#e1eaff' }}>
                    <Shield className="w-4 h-4" style={{ color: '#3370ff' }} />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-medium mb-1" style={{ color: '#1f2329' }}>
                      {t.organization.roles.aboutSystemRoles}
                    </div>
                    <div className="text-sm" style={{ color: '#646a73' }}>
                      {t.organization.roles.systemRolesNote}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen} data-testid="delete-role-dialog">
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.organization.roles.confirmDeleteRole}</DialogTitle>
            <DialogDescription>
              {t.organization.roles.confirmDeleteRoleMessage} <strong>{roleToDelete?.name}</strong> {t.organization.roles.confirmDeleteRoleSuffix}
              <br />
              {t.organization.roles.cannotUndo}
              {roleToDelete && (roleToDelete.userCount || 0) > 0 && (
                <div className="mt-2 p-2 rounded text-sm" style={{ backgroundColor: '#fff7e6', border: '1px solid #ffe7ba', color: '#d46b08' }}>
                  ⚠️ {t.organization.roles.roleHasMembers} {roleToDelete.userCount} {t.organization.roles.membersWillLosePermissions}
                </div>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={deleting}
              data-action="cancel"
            >
              {t.common.cancel}
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={deleting}
              data-action="confirm-delete"
            >
              {deleting ? t.common.deleting : t.common.confirmDelete}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
